import React, { useState } from 'react';
import { INSTAGRAM_POSTS } from '../data/instagramPosts';

/**
 * InstagramCaptionLibrary, copy-paste-ready library of Instagram captions.
 *
 * Mirrors the LinkedIn library pattern but tuned for Instagram:
 *  - Visual brief is exposed (so the marketer knows what photo to take)
 *  - Cadence + format shown explicitly (square / portrait, day of week)
 *  - Two copy buttons: caption only, or caption + hashtags
 *
 * Scroll-only. Hidden from print.
 */

const COLORS = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  ink: '#0A2540',
  slate: '#2A3B5A',
  muted: '#5B6B82',
  border: '#E5DFD3',
  bgSoft: '#F7F4EE',
  cream: '#FFF8E7',
  magenta: '#D6336C',
  gold: '#C99A2E',
  emerald: '#1F7A4C',
};

export default function InstagramCaptionLibrary() {
  const [copiedKey, setCopiedKey] = useState(null);

  async function copyText(key, text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2200);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2200);
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
    }
  }

  return (
    <section
      className="no-print"
      data-testid="instagram-caption-library"
      style={{
        background: '#fdf8ef',
        padding: '64px 24px 96px',
      }}
    >
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              color: COLORS.magenta,
              fontWeight: 700,
              marginBottom: 12,
            }}
          >
            {INSTAGRAM_POSTS.length} ready-to-publish Instagram captions
          </div>
          <h2
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 36,
              fontWeight: 700,
              color: COLORS.navy,
              letterSpacing: '-0.02em',
              lineHeight: 1.05,
              margin: '0 0 14px',
            }}
          >
            Instagram captions.{' '}
            <em style={{ color: COLORS.emerald, fontStyle: 'italic', fontWeight: 500 }}>
              Photo brief included.
            </em>
          </h2>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.55,
              color: COLORS.slate,
              maxWidth: 680,
              margin: 0,
            }}
          >
            Each caption maps to one of the 5 Instagram pillars (Floor First · Tools, Not Theatre ·
            Cheese is Craft · Excel vs Reality · The Team). Each card includes the visual brief so
            you know what photo to take, the recommended posting cadence, the full caption, and
            the hashtag bank. Two copy buttons per card: caption only, or caption + hashtags.
          </p>
        </div>

        {/* Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {INSTAGRAM_POSTS.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onCopy={copyText}
              copiedKey={copiedKey}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function PostCard({ post, onCopy, copiedKey }) {
  const captionOnlyKey = `${post.id}__caption`;
  const captionPlusKey = `${post.id}__full`;
  return (
    <article
      data-testid={`ig-card-${post.id}`}
      style={{
        background: '#fff',
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow: '0 6px 24px rgba(10, 37, 64, 0.08)',
        border: `1px solid ${COLORS.border}`,
      }}
    >
      {/* Header strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px',
          background: COLORS.bgSoft,
          borderBottom: `1px solid ${COLORS.border}`,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '4px 12px',
              background: post.color,
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: '0.06em',
              borderRadius: 4,
              textTransform: 'uppercase',
            }}
          >
            {post.pillar} · {post.pillarName}
          </span>
          <span style={{ fontSize: 11.5, color: COLORS.muted }}>{post.format}</span>
          <span style={{ fontSize: 11.5, color: COLORS.muted }}>· {post.cadence}</span>
        </div>
      </div>

      {/* Visual brief OR generated image */}
      {post.image ? (
        <div
          style={{
            position: 'relative',
            background: '#000',
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <img
            src={post.image}
            alt={post.visualBrief}
            data-testid={`ig-image-${post.id}`}
            style={{
              width: '100%',
              maxHeight: 520,
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              padding: '4px 10px',
              background: 'rgba(0,0,0,0.65)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              color: '#fff',
              fontSize: 10,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              fontWeight: 700,
              borderRadius: 3,
            }}
          >
            🎨 Generated · {post.imageStyleTag}
          </div>
          <details
            style={{
              padding: '12px 20px',
              background: '#fbf8f1',
              color: COLORS.slate,
              fontSize: 12.5,
              lineHeight: 1.55,
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                fontSize: 10,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 800,
                color: COLORS.gold,
              }}
            >
              📷 Photo / illustration brief
            </summary>
            <div style={{ marginTop: 6 }}>{post.visualBrief}</div>
          </details>
        </div>
      ) : (
        <div
          style={{
            padding: '14px 20px',
            background: '#fbf8f1',
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div
            style={{
              fontSize: 10,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 800,
              color: COLORS.gold,
              marginBottom: 4,
            }}
          >
            📷 Visual brief
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: COLORS.slate }}>
            {post.visualBrief}
          </div>
        </div>
      )}

      {/* Title */}
      <div style={{ padding: '16px 20px 6px' }}>
        <div
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 19,
            fontWeight: 700,
            color: COLORS.navy,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
          }}
        >
          {post.title}
        </div>
      </div>

      {/* Caption body */}
      <div
        style={{
          padding: '6px 20px 16px',
          fontFamily:
            "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 14.5,
          lineHeight: 1.6,
          color: COLORS.ink,
          whiteSpace: 'pre-wrap',
        }}
      >
        {post.body}
      </div>

      {/* Hashtag strip */}
      <div
        style={{
          padding: '12px 20px',
          background: '#fcfaf6',
          borderTop: `1px solid ${COLORS.border}`,
          fontSize: 12.5,
          color: COLORS.magenta,
          fontWeight: 600,
          letterSpacing: '0.01em',
          lineHeight: 1.65,
        }}
      >
        {post.hashtags}
      </div>

      {/* Copy buttons */}
      <div
        style={{
          padding: '12px 20px',
          background: '#fff',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={() => onCopy(captionOnlyKey, post.body)}
          data-testid={`copy-caption-${post.id}`}
          style={copyBtnStyle(copiedKey === captionOnlyKey, COLORS.navy)}
        >
          {copiedKey === captionOnlyKey ? '✓ Caption copied' : 'Copy caption only'}
        </button>
        <button
          onClick={() =>
            onCopy(captionPlusKey, `${post.body}\n\n${post.hashtags}`)
          }
          data-testid={`copy-full-${post.id}`}
          style={copyBtnStyle(copiedKey === captionPlusKey, COLORS.magenta)}
        >
          {copiedKey === captionPlusKey
            ? '✓ Caption + hashtags copied'
            : 'Copy caption + hashtags'}
        </button>
      </div>
    </article>
  );
}

function copyBtnStyle(active, baseColor) {
  return {
    padding: '8px 16px',
    background: active ? COLORS.emerald : baseColor,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.04em',
    cursor: 'pointer',
    textTransform: 'uppercase',
    transition: 'background 0.15s ease',
  };
}
